import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Wallet, AlertTriangle, TrendingUp, TrendingDown, CalendarRange, Archive } from "lucide-react";
import { Panel } from "@/components/core/AppLayout";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { eur, MESI, fmtDay } from "@/lib/invoices";

const Delta = ({ cur, prev, testid }) => {
  if (!prev) return <span className="text-xs text-slate-500" data-testid={testid}>nessun dato l'anno scorso</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${up ? "text-emerald-700" : "text-[#C8102E]"}`} data-testid={testid}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{up ? "+" : ""}{pct}% vs {eur(prev)}
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, children, danger, testid }) => (
  <div className="rounded-2xl bg-white/70 border border-[#5FB3EA]/60 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-600"><Icon className={`h-4 w-4 ${danger ? "text-[#E8173A]" : "text-[#0A1128]"}`} />{label}</div>
    <div className={`font-display text-2xl font-black mt-1 ${danger ? "text-[#C8102E]" : "text-[#0A1128]"}`} data-testid={`${testid}-value`}>{value}</div>
    <div className="mt-1">{children}</div>
  </div>
);

export const EconomicDashboard = () => {
  const { user } = useAuth();
  const { data: d } = useFetch("/fatture/dashboard");
  if (!d) return null;
  const now = new Date();
  const chart = d.mensile.map((m) => ({ nome: `${MESI[Number(m.mese.slice(5)) - 1].slice(0, 3)} ${m.mese.slice(2, 4)}`, Fatturato: m.totale, "Anno precedente": m.anno_prec }));
  return (
    <Panel className="p-6 mb-8 core-rise" data-testid="economic-dashboard">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8173A]">Economia</div>
          <h2 className="font-display text-xl font-black text-[#0A1128]">{user.role === "admin" ? "Fatture di tutti i soci" : "Le tue fatture"}</h2>
        </div>
        <Link to="/amministrazione/fatture" className="text-sm font-bold text-[#0A1128] hover:text-[#E8173A]" data-testid="eco-open-invoices">Vai alle fatture →</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 core-m2">
        <Stat icon={Wallet} label="Da incassare" value={eur(d.da_incassare)} testid="eco-da-incassare"><span className="text-xs text-slate-600">{d.da_incassare_count} fatture da pagare</span></Stat>
        <Stat icon={AlertTriangle} label="Scadute" value={eur(d.scadute_totale)} danger={d.scadute_count > 0} testid="eco-scadute"><span className="text-xs text-slate-600">{d.scadute_count} fatture</span></Stat>
        <Stat icon={CalendarRange} label={`Fatturato ${MESI[now.getMonth()].toLowerCase()}`} value={eur(d.mese)} testid="eco-mese"><Delta cur={d.mese} prev={d.mese_anno_prec} testid="eco-mese-delta" /></Stat>
        <Stat icon={CalendarRange} label={`Fatturato ${d.anno_corrente}`} value={eur(d.anno)} testid="eco-anno"><Delta cur={d.anno} prev={d.anno_prec} testid="eco-anno-delta" /></Stat>
      </div>
      <div className="text-[11px] text-slate-500 -mt-3 mb-4">Confronto con lo stesso periodo dell'anno precedente (fino a oggi).
        {d.archiviati.fatture > 0 && <span className="inline-flex items-center gap-1 ml-2 text-amber-800" data-testid="eco-archived-note"><Archive className="h-3 w-3" />Inclusi clienti archiviati: {eur(d.archiviati.anno)} fatturato {d.anno_corrente}, {eur(d.archiviati.da_incassare)} da incassare.</span>}
      </div>
      <div className="grid grid-cols-[1.5fr_1fr] gap-5">
        <div data-testid="eco-chart">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Ultimi 12 mesi</div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#5FB3EA" vertical={false} />
              <XAxis dataKey="nome" tick={{ fill: "#0A1128", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#0A1128", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip formatter={(v) => eur(v)} cursor={{ fill: "#A6DAFC" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Fatturato" fill="#E8173A" radius={[5, 5, 0, 0]} />
              <Bar dataKey="Anno precedente" fill="#0A1128" fillOpacity={0.25} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="eco-overdue">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Scadute da più tempo</div>
          {!d.scadute_top.length && <div className="text-sm text-slate-600 py-6">Nessuna fattura scaduta.</div>}
          <div className="space-y-1.5">
            {d.scadute_top.map((f) => (
              <Link key={f.id} to={`/amministrazione/fatture?cliente=${f.client_id}`} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 hover:bg-[#A6DAFC]" data-testid={`eco-overdue-${f.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-[#0A1128] truncate">{f.client_nome}{f.archiviato && <span className="ml-1.5 text-[10px] font-bold text-amber-800">ARCHIVIATO</span>}</div>
                  <div className="text-xs text-slate-600">n. {f.numero} · scaduta il {fmtDay(f.scadenza)}{user.role === "admin" ? ` · ${f.owner_nome}` : ""}</div>
                </div>
                <div className="text-right shrink-0"><div className="font-bold text-sm text-[#0A1128]">{eur(f.importo)}</div><div className="text-[11px] font-bold text-[#C8102E]">{f.giorni} gg</div></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      {user.role === "admin" && d.per_socio?.length > 0 && (
        <table className="w-full text-sm mt-5" data-testid="eco-per-socio">
          <thead><tr className="text-left text-xs text-slate-600"><th className="pb-2">Socio</th><th className="pb-2 text-right">Fatturato {d.anno_corrente}</th><th className="pb-2 text-right">Stesso periodo {d.anno_corrente - 1}</th><th className="pb-2 text-right">Da incassare</th><th className="pb-2 text-right">Scadute</th></tr></thead>
          <tbody>{d.per_socio.map((p) => (
            <tr key={p.socio_id} className="border-t border-[#5FB3EA]/60" data-testid={`eco-socio-${p.socio_id}`}><td className="py-2 font-bold text-[#0A1128]">{p.socio_nome}</td>
              <td className="py-2 text-right">{eur(p.anno)}</td><td className="py-2 text-right text-slate-600">{eur(p.anno_prec)}</td><td className="py-2 text-right">{eur(p.da_incassare)}</td>
              <td className="py-2 text-right">{p.scadute_count} · {eur(p.scadute_totale)}</td></tr>
          ))}</tbody>
        </table>
      )}
    </Panel>
  );
};
